/**
 * Three screens, one shell.
 *
 *   kikik.id  ->  Kikik Mahjong  ->  the table
 *
 * The portal and the game deliberately look nothing alike: the portal is warm
 * paper and the table is dark felt, and the switch happens on `data-view` at
 * the document root so neither one's styling can leak into the other. What
 * carries across is the header logic - brand, language, a way back - so a
 * player is never stranded at a table with no exit.
 */

import { useEffect, useState } from "react";
import type { AppMode, ShellNav } from "./components";
import { LocaleProvider, useDocumentTitle } from "./i18n";
import type { Locale } from "@kikik/i18n";
import { OnlineMode, SoloMode, TutorialMode } from "./modes";
import { GameEntry, PortalHome, useView } from "./portal";
import type { NavTarget, View } from "./portal";

export interface AppProps {
  /** Deep link straight to a table. Omit it and the visitor lands on kikik.id. */
  mode?: AppMode;
  /** Explicit starting screen; `mode` is the shorthand for a table. */
  view?: View;
  /** Mirror the current screen in `location.hash`. On in the browser, off in tests. */
  routing?: boolean;
  /** Test hooks. */
  seed?: string;
  botDelayMs?: number;
  serverUrl?: string;
  autoConnect?: boolean;
  /** Test hook; normally the stored choice, or Bahasa Indonesia. */
  locale?: Locale;
}

/**
 * The table screen.
 *
 * There is no longer a page around the game: each mode renders `GameShell`
 * itself, which is a fixed 100dvh frame with one thin bar and a stage. This
 * function only decides which mode is on, and hands it the two things the bar
 * needs to navigate with.
 */
function PlayScreen({
  mode,
  onMode,
  onExit,
  seed,
  botDelayMs,
  serverUrl,
  autoConnect,
}: {
  mode: AppMode;
  onMode: (mode: AppMode) => void;
  onExit: () => void;
} & Pick<AppProps, "seed" | "botDelayMs" | "serverUrl" | "autoConnect">) {
  useDocumentTitle("app.documentTitle");
  const nav: ShellNav = { onMode, onExit };

  if (mode === "belajar") {
    return <TutorialMode nav={nav} {...(botDelayMs === undefined ? {} : { botDelayMs })} />;
  }
  if (mode === "online") {
    return (
      <OnlineMode
        nav={nav}
        {...(serverUrl ? { url: serverUrl } : {})}
        {...(autoConnect === undefined ? {} : { autoConnect })}
      />
    );
  }
  return (
    <SoloMode
      nav={nav}
      {...(seed ? { seed } : {})}
      {...(botDelayMs === undefined ? {} : { botDelayMs })}
    />
  );
}

function HomeScreen(props: {
  onOpenGame: (slug: string) => void;
  onPlay: (mode: AppMode) => void;
  scrollTo: NavTarget | null;
}) {
  useDocumentTitle("portal.documentTitle");
  return (
    <PortalHome
      onOpenGame={props.onOpenGame}
      onPlaySolo={() => props.onPlay("solo")}
      onLearn={() => props.onPlay("belajar")}
      scrollTo={props.scrollTo}
    />
  );
}

function GameScreen(props: {
  slug: string;
  onHome: () => void;
  onNavigateHome: (target: NavTarget) => void;
  onPlay: (mode: AppMode) => void;
  onOpenGame: (slug: string) => void;
}) {
  useDocumentTitle("portal.game.mahjong.title");
  return <GameEntry {...props} />;
}

function AppShell({ mode, view, routing, ...play }: AppProps) {
  const initial: View = view ?? (mode ? { name: "play", mode } : { name: "home" });
  const [screen, go] = useView(initial, routing ?? false);
  const [scrollTo, setScrollTo] = useState<NavTarget | null>(null);

  // Arriving on a new screen means the top of it, not wherever the last one
  // was scrolled to. Hash routing does not do this on its own.
  useEffect(() => {
    if (screen.name !== "play") return;
    const root = globalThis.document?.documentElement;
    if (root) root.scrollTop = 0;
  }, [screen.name]);

  useEffect(() => {
    const root = globalThis.document?.documentElement;
    if (!root) return;
    root.dataset.view = screen.name === "play" ? "game" : "portal";
  }, [screen.name]);

  const goHome = (target: NavTarget | null = null): void => {
    setScrollTo(target);
    go({ name: "home" });
  };

  if (screen.name === "play") {
    return (
      <PlayScreen
        mode={screen.mode}
        onMode={(next) => go({ name: "play", mode: next })}
        onExit={() => goHome()}
        {...play}
      />
    );
  }

  if (screen.name === "game") {
    return (
      <GameScreen
        slug={screen.slug}
        onHome={() => goHome()}
        onNavigateHome={(target) => goHome(target)}
        onPlay={(next) => go({ name: "play", mode: next })}
        onOpenGame={(slug) => go({ name: "game", slug })}
      />
    );
  }

  return (
    <HomeScreen
      onOpenGame={(slug) => go({ name: "game", slug })}
      onPlay={(next) => go({ name: "play", mode: next })}
      scrollTo={scrollTo}
    />
  );
}

export function App({ locale, ...props }: AppProps = {}) {
  return (
    <LocaleProvider {...(locale ? { locale } : {})}>
      <AppShell {...props} />
    </LocaleProvider>
  );
}
